import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Truck,
  MapPin,
  IndianRupee,
  CreditCard,
  MessageSquare,
  FileText,
  Settings,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Car,
  ShieldAlert,
  ShieldCheck,
  Bell,
  Tag,
  Gift,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/uiStore";
import { useAuthStore } from "@/stores/authStore";
import { ROUTES } from "@/constants/routes";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";

interface NavItem {
  label: string;
  icon: React.ElementType;
  href?: string;
  permission?: string;
  children?: {
    label: string;
    href: string;
  }[];
}

const navigation: NavItem[] = [
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    href: ROUTES.DASHBOARD,
    permission: "dashboard",
  },
  {
    label: "Customers",
    icon: Users,
    href: ROUTES.CUSTOMERS,
    permission: "customers",
  },
  {
    label: "Drivers",
    icon: Truck,
    permission: "drivers",
    children: [
      { label: "All Drivers", href: ROUTES.DRIVERS },
      { label: "KYC Pending", href: ROUTES.KYC_PENDING },
    ],
  },
  {
    label: "Vehicles",
    icon: Car,
    permission: "vehicles",
    children: [
      { label: "All Vehicles", href: ROUTES.VEHICLES },
      { label: "Pending Verifications", href: ROUTES.VEHICLE_PENDING },
    ],
  },
  {
    label: "Bookings",
    icon: MapPin,
    href: ROUTES.BOOKINGS,
    permission: "bookings",
  },
  {
    label: "Pricing",
    icon: IndianRupee,
    permission: "pricing",
    children: [
      { label: "Fare Rules", href: ROUTES.FARE_RULES },
    ],
  },
  {
    label: "Payments",
    icon: CreditCard,
    permission: "payments",
    children: [
      { label: "All Payments", href: ROUTES.PAYMENTS },
      { label: "Driver Earnings", href: ROUTES.DRIVER_EARNINGS },
      { label: "Payouts", href: ROUTES.PAYOUTS },
    ],
  },
  {
    label: "Support",
    icon: MessageSquare,
    href: ROUTES.SUPPORT,
    permission: "support",
  },
  {
    label: "Reports",
    icon: FileText,
    permission: "reports",
    children: [
      { label: "Booking Reports", href: ROUTES.BOOKING_REPORTS },
      { label: "Revenue Reports", href: ROUTES.REVENUE_REPORTS },
      { label: "Driver Reports", href: ROUTES.DRIVER_REPORTS },
      { label: "GST Report", href: ROUTES.GST_REPORT },
    ],
  },
  {
    label: "Fraud & Safety",
    icon: ShieldAlert,
    permission: "fraud",
    children: [
      { label: "Fraud Reports", href: ROUTES.FRAUD_REPORTS },
      { label: "Unblock Requests", href: ROUTES.UNBLOCK_REQUESTS },
      { label: "Fraud Intelligence", href: ROUTES.FRAUD_INTELLIGENCE },
    ],
  },
  {
    label: "Bookfleet Shield",
    icon: ShieldCheck,
    href: ROUTES.ZIPTO_SHIELD,
    permission: "admin",
  },
  {
    label: "Push Notifications",
    icon: Bell,
    href: ROUTES.PUSH_NOTIFICATIONS,
    permission: "notifications",
  },
  {
    label: "Coupons",
    icon: Tag,
    href: ROUTES.COUPONS,
    permission: "admin",
  },
  {
    label: "Referrals",
    icon: Gift,
    href: ROUTES.REFERRALS,
    permission: "admin",
  },
];

// ─── NavItemContent defined OUTSIDE Sidebar so React never remounts it ────────

interface NavItemProps {
  item: NavItem;
  isExpanded: boolean;
  isCollapsed: boolean;
  onToggle: (label: string) => void;
  isActive: (href: string) => boolean;
  isGroupActive: (children?: { href: string }[]) => boolean;
}

function NavItemContent({
  item,
  isExpanded,
  isCollapsed,
  onToggle,
  isActive,
  isGroupActive,
}: NavItemProps) {
  const Icon = item.icon;
  const hasChildren = Boolean(item.children?.length);
  const active = item.href ? isActive(item.href) : isGroupActive(item.children);

  const iconAndLabel = (
    <>
      <Icon className={cn("h-5 w-5 shrink-0", active && "text-primary")} />
      {!isCollapsed && (
        <>
          <span className="flex-1 text-left">{item.label}</span>
          {hasChildren && (
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 transition-transform",
                isExpanded && "rotate-180",
              )}
            />
          )}
        </>
      )}
    </>
  );

  if (isCollapsed && !hasChildren) {
    return (
      <Tooltip content={item.label} side="right">
        <Link
          to={item.href!}
          className={cn(
            "flex items-center justify-center p-2 rounded-md transition-colors",
            active
              ? "bg-sidebar-accent text-sidebar-accent-foreground"
              : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          )}
        >
          {iconAndLabel}
        </Link>
      </Tooltip>
    );
  }

  if (hasChildren) {
    return (
      <>
        <button
          type="button"
          onClick={() => onToggle(item.label)}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
            active
              ? "bg-sidebar-accent text-sidebar-accent-foreground"
              : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            isCollapsed && "justify-center",
          )}
        >
          {iconAndLabel}
        </button>
        {!isCollapsed && isExpanded && (
          <div className="ml-6 mt-1 space-y-1 border-l border-sidebar-border pl-3">
            {item.children?.map((child) => (
              <Link
                key={child.href}
                to={child.href}
                className={cn(
                  "block rounded-md px-3 py-2 text-sm transition-colors",
                  isActive(child.href)
                    ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                    : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                )}
              >
                {child.label}
              </Link>
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <Link
      to={item.href!}
      className={cn(
        "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
        active
          ? "bg-sidebar-accent text-sidebar-accent-foreground"
          : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        isCollapsed && "justify-center",
      )}
    >
      {iconAndLabel}
    </Link>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export function Sidebar() {
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const { hasPermission } = useAuthStore();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  const isActive = React.useCallback(
    (href: string) => {
      if (href === "/") return location.pathname === "/";
      return location.pathname.startsWith(href);
    },
    [location.pathname],
  );

  const isGroupActive = React.useCallback(
    (children?: { href: string }[]) => children?.some((c) => isActive(c.href)) ?? false,
    [isActive],
  );

  const toggleExpanded = React.useCallback((label: string) => {
    setExpandedItems((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  }, []);

  // Auto-expand the group that contains the current route (on first load / navigation).
  React.useEffect(() => {
    setExpandedItems((prev) => {
      const toAdd = navigation
        .filter((item) => item.children && item.children.some((c) => isActive(c.href)))
        .map((item) => item.label)
        .filter((label) => !prev.includes(label));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  }, [isActive]);

  const filteredNavigation = navigation.filter(
    (item) => !item.permission || hasPermission(item.permission),
  );

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 flex h-screen flex-col border-r bg-sidebar transition-all duration-300",
        sidebarCollapsed ? "w-[68px]" : "w-64",
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
        {!sidebarCollapsed && (
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/zipto-logo.png"
              alt="BOOKFLEET Logo"
              className="h-8 w-8 object-contain"
            />
            <span className="font-bold text-lg">BOOKFLEET</span>
          </Link>
        )}
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={toggleSidebar}
          className={cn("shrink-0", sidebarCollapsed && "mx-auto")}
        >
          {sidebarCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 overflow-y-auto p-3 scrollbar-hide">
        {filteredNavigation.map((item) => (
          <NavItemContent
            key={item.label}
            item={item}
            isExpanded={expandedItems.includes(item.label)}
            isCollapsed={sidebarCollapsed}
            onToggle={toggleExpanded}
            isActive={isActive}
            isGroupActive={isGroupActive}
          />
        ))}
      </nav>

      {/* Settings */}
      <div className="border-t border-sidebar-border p-3">
        {sidebarCollapsed ? (
          <Tooltip content="Settings" side="right">
            <Link
              to={ROUTES.SETTINGS}
              className={cn(
                "flex items-center justify-center p-2 rounded-md transition-colors",
                isActive(ROUTES.SETTINGS)
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              )}
            >
              <Settings className="h-5 w-5" />
            </Link>
          </Tooltip>
        ) : (
          <Link
            to={ROUTES.SETTINGS}
            className={cn(
              "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              isActive(ROUTES.SETTINGS)
                ? "bg-sidebar-accent text-sidebar-accent-foreground"
                : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            )}
          >
            <Settings className="h-5 w-5" />
            <span>Settings</span>
          </Link>
        )}
      </div>
    </aside>
  );
}